import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { MenuItem } from "@mui/material"
import { fileToBase64 } from "lib/file-to-base64"
import { cobieUpdateRegister } from "../asset-mapping/plugins/cobie-update-register/controller/cobie-update-register"
import { busyWhile } from "lib/@components/busy/busy-while"
import { useDialog } from "lib/@hooks/useDialog"
import { ExcelImportDialog } from "./excel-import-dialog"
import { navigate } from "lib/routes/navigate"
import { CobieImportDialog } from "./cobie-import-dialog"
import { BasicMenu } from "lib/@components/basic-menu/basic-menu"
import { IfTooltip } from "lib/if-tooltip"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { useIsAnythingBeingMapped } from "routes/asset-register/plugins/asset-mapping/use-is-anything-being-mapped"
import { useIsAnythingRefining } from "routes/asset-register/plugins/asset-mapping/use-is-anything-refining"
import { useDemand } from "lib/authorization/use-demand"
import { isEnabled } from "lib/@components/feature"

export function AssetFileImporterButton({ title }) {
    const { register, refreshRegister, createAsset } = useBoundContext()
    const isMapping = useIsAnythingBeingMapped()
    const isRefining = useIsAnythingRefining()
    const excelFileSelector = useDialog(<ExcelImportDialog />)
    const cobieFileSelector = useDialog(<CobieImportDialog />)
    const isMappingMsg =
        "This file is currently being processed by Mobiliser and cannot be updated until processing is complete."
    const canManageRegime = useDemand("manageRegime")
    const canAddSingleAsset =
        !!createAsset && canManageRegime && (isEnabled("edit-assets") || isEnabled("edit-assets-all-users"))

    return (
        <IfTooltip condition={isMapping || isRefining} title={isMappingMsg}>
            <BasicMenu
                disabled={isMapping || isRefining}
                label={title}
                sx={{ mr: 3 }}
                startIcon="ic:round-plus"
                buttonDataCy="asset-register-add-file-button"
                menuDataCy="asset-register-add-file-menu"
            >
                {canAddSingleAsset && (
                    <MenuItem onClick={createAsset} data-cy="asset-register-add-single-asset-button">
                        Add Single Asset
                    </MenuItem>
                )}
                <MenuItem onClick={handleExcelFile} data-cy="asset-register-add-excel-file-button">
                    Add via Excel File
                </MenuItem>
                <MenuItem onClick={handleCobieFile} data-cy="asset-register-add-cobie-file-button">
                    Add via COBie File
                </MenuItem>
            </BasicMenu>
        </IfTooltip>
    )

    async function handleCobieFile() {
        const result = await cobieFileSelector()
        if (result?.files?.length > 0) {
            const selectedFile = result.files[0]
            try {
                await busyWhile(
                    async () =>
                        cobieUpdateRegister({
                            file: await fileToBase64(selectedFile),
                            registerId: register?.id,
                            fileSize: selectedFile?.size,
                        }),
                    "Adding COBie file assets"
                )
            } catch (e) {
                console.error(e.message)
                errorSnackbar(`failed to import COBie file: ${e.message}`)
                return
            }
            refreshRegister()
        }
    }

    async function handleExcelFile() {
        const result = await excelFileSelector()
        if (result?.files?.length > 0) {
            const selectedFile = result.files[0]
            navigate(`/app/assets/${register?.id}/add-assets`, { state: { file: selectedFile } })
        }
    }
}
